import { useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { DataTable, useDataTableContext } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { uniqueArray } from "@msuite/utils/array";
import { canMoveSuborderStateAsManager, getCanGoForwardSuborderState } from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import type { SuborderListItem } from "@msuite/utils/types";
import { client } from "@/hono";

/** Props Interface */
interface SuborderActionProps {
	item: SuborderListItem;
}

export function SuborderAction({ item }: SuborderActionProps) {
	/** Hooks */
	const { refetch, table } = useDataTableContext();
	const loader = useLoading();
	const { t } = useTranslation();

	/** Functions */
	/**
	 * The rounds this click moves: the clicked row, plus every selected row the manager's lane
	 * still covers. The selection is filtered rather than passed through, because it can hold a
	 * round a picker has already taken while the button belongs to one that is still open.
	 */
	function collectMovableSuborderIds() {
		const suborderIds = [item.id];
		for (const row of table.getSelectedRowModel().rows) {
			const suborder = row.original as SuborderListItem;
			if (!canMoveSuborderStateAsManager(suborder.state)) continue;
			suborderIds.push(suborder.id);
		}
		return uniqueArray(suborderIds);
	}

	async function handleOnProcess() {
		try {
			loader.start();
			const suborderIds = collectMovableSuborderIds();
			// Every selected suborder is attempted, so one that the server refuses does
			// not hold up the rest, and the refetch afterwards shows whichever moved.
			// The first failure is kept and reported: the batch fails identically on
			// every row when the caller lacks `update Stock`, and one toast per row
			// would bury the answer.
			const failures: unknown[] = [];
			for (const suborderId of suborderIds) {
				try {
					const response = await client.stock.orders[":orderId"].suborders[":suborderId"].$patch({
						param: { orderId: item.orderId, suborderId },
						query: {
							role: "manager",
						},
					});
					await responseHandler(response, false);
				} catch (error) {
					failures.push(error);
				}
			}
			table.resetRowSelection();
			await refetch?.();
			const [failure] = failures;
			if (failure) throw failure;
		} catch (error) {
			console.error(error);
			toast.error(error instanceof Error ? error.message : t("general.request_failed"));
		} finally {
			loader.stop();
		}
	}

	/** Computed */
	// The lane decides whether the control exists at all; the direction inside it decides whether
	// this round is released or recalled.
	const canMove = canMoveSuborderStateAsManager(item.state);
	const canGoForward = getCanGoForwardSuborderState(item.state, "manager");

	/** Guard */
	if (!canMove) return guardString(null);

	/** Render */
	return (
		<DataTable.Button
			icon={<Icon name={canGoForward ? "ArrowRight" : "ArrowLeft"} />}
			onClick={handleOnProcess}
			isLoading={loader.isLoading}
		>
			{canGoForward ? t("stock.release_order") : t("stock.recall_order")}
		</DataTable.Button>
	);
}
